import React, { Fragment, useEffect, useReducer, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";
import { Datepicker, momentTimezone } from "@mobiscroll/react";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment-timezone";
import Select from "react-select";

import axios from "axios";
import ai from "../../helpers/axios";
import columns from "./quotes-columns";
import Search from "../search";
import DataTableServerPagination from "../data-table-server-pagination";

import PageHeaderRowSearch from "../shared/page-header-row-search";

const deliveryFilterOptions = [
  { label: "Draft", value: "draft" },
  { label: "Sent", value: "sent" },
  { label: "Synced", value: "synced" },
];

const reducer = (state, action) => {
  switch (action.type) {
    case "search": {
      return {
        ...state,
        query: action.query,
      };
    }
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: action.payload.loadingStatus,
      };
    }
    case "rowSelection": {
      return {
        ...state,
        selectedRows: action.payload.selectedRows,
        allSelected: action.payload.allSelected,
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        quotes: {
          data: action.payload.data.quotes,
        },
        loading: false,
        totalLength: action.payload.data.countTotal,
      };
    }
    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }
    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
      };
    }
    case "stageFilterChanged": {
      return {
        ...state,
        stageFilters: action.payload.values,
      };
    }
    case "deliveryFilterChanged": {
      return {
        ...state,
        deliveryFilters: action.payload.values,
      };
    }
    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
      };
    }

    default:
      break;
  }
  return state;
};

const initialState = {
  quotes: {
    data: [],
  },
  loading: true,
  countPerPage: 25,
  currentPage: 1,
  selectedRows: [],
  allSelected: false,
  tagFilters: [],
  totalLength: null,
  clearSelectedRows: false,
  ownerInputField: "",
  selectedOwner: null,
  query: "",
  sortDirection: "desc",
  sortBy: "createdAt",
  deliveryFilters: [],
  startDate: moment().subtract(1, "year").toDate(),
  endDate: null,
};

const QuotesContent = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  const abortControllerRef = useRef(null);

  const getData = async (
    dbQuery,
    loadSilently,
    stageFilters,
    sortBy,
    sortDirection,
    deliveryFilters,
  ) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    let loadingStatus = !loadSilently;

    dispatch({ type: "dataBeganDownloading", payload: { loadingStatus } });

    try {
      const token = await getAccessTokenSilently().catch((e) => {
        console.error(e);
        return loginWithRedirect();
      });

      const { data } = await ai.auth(token).get(`/api/quotes/list`, {
        signal,
        params: {
          query: dbQuery,
          page: state.currentPage,
          countPerPage: state.countPerPage,
          sortBy,
          sortDirection,
          stageFilters,
          deliveryFilters,
          startDate: state.startDate,
          endDate: state.endDate,
        },
      });

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
        },
      });
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error(error);
    }
  };

  useEffect(() => {
    getData(
      state.query,
      false,
      state.stageFilters,
      state.sortBy,
      state.sortDirection,
      state.deliveryFilters,
    );

    document.title = "Quote Management | evoJets HQ";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPage, state.countPerPage]);

  const handleDateRangeChange = (values) => {
    const [startDate, endDate] = values || [];

    dispatch({
      type: "fieldChanged",
      field: "startDate",
      value: startDate || null,
    });

    dispatch({
      type: "fieldChanged",
      field: "endDate",
      value: endDate || null,
    });
  };

  const handleDateRangeSelected = () => {
    getData(
      state.query,
      false,
      state.stageFilters,
      state.sortBy,
      state.sortDirection,
      state.deliveryFilters,
    );
  };

  const handlePageChange = (page, totalRows) => {
    console.log(page, totalRows);

    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  };

  const handleSelection = function (rowData) {
    dispatch({
      type: "rowSelection",
      payload: {
        selectedRows: rowData.selectedRows,
        allSelected: rowData.allSelected,
      },
    });
  };

  const debouncedGetData = useDebouncedCallback(getData, 500);

  const handleSearch = (e) => {
    let query = e.target.value.toLowerCase();

    dispatch({ type: "search", query });
    debouncedGetData(
      query,
      true,
      state.stageFilters,
      state.sortBy,
      state.sortDirection,
      state.deliveryFilters,
    );
  };

  const handleDeliveryFilterChange = (selectedOptions) => {
    const values = selectedOptions?.length
      ? selectedOptions.map((option) => option.value)
      : [];

    getData(
      state.query,
      false,
      state.stageFilters,
      state.sortBy,
      state.sortDirection,
      values,
    );

    dispatch({
      type: "deliveryFilterChanged",
      payload: { values },
    });
  };

  const handleSort = (column, sortDirection) => {
    console.log("sorted", column.selector, sortDirection);

    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection,
      },
    });

    getData(
      state.query,
      true,
      state.stageFilters,
      column.selector,
      sortDirection,
      state.deliveryFilters,
    );

    //keyField is required for server sort
  };

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1={`Quote Management`}
        pretitle="Business"
        breadcrumbs={[{ link: "/leads", label: "Leads" }]}
        rightColumn={
          <div id="quote-actions-row">
            <div className="quotes-date-range-container">
              <Datepicker
                select="range"
                dataTimezone="utc"
                displayTimezone="local"
                timezonePlugin={momentTimezone}
                value={[state.startDate, state.endDate]}
                onChange={(event) => {
                  handleDateRangeChange(event.value);
                }}
                onClose={handleDateRangeSelected}
                controls={["date"]}
                placeholder="Select range"
              />
            </div>

            <Search
              onSearch={handleSearch}
              placeholder="Search Quotes"
              query={state.query}
              className="quote-search"
            />

            <Select
              options={deliveryFilterOptions}
              isMulti
              className="delivery-filter"
              classNamePrefix="Select"
              placeholder="Filter by Delivery"
              value={deliveryFilterOptions.filter((option) =>
                state.deliveryFilters.includes(option.value)
              )}
              onChange={handleDeliveryFilterChange}
              isClearable={true}
              closeMenuOnSelect={false}
            />
          </div>
        }
      />

      <DataTableServerPagination
        onSelection={handleSelection}
        columns={columns}
        handlePageChange={handlePageChange}
        handleRowsPerPageChange={handleRowsPerPageChange}
        state={state}
        data={state.quotes.data}
        defaultSortField={"createdAt"}
        defaultSortAsc={false}
        handleSort={handleSort}
        sortServer={true}
      />
    </Fragment>
  );
};

export default QuotesContent;
